import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { formatDate } from "@/lib/format";

export const SUBMISSION_TIMEZONE = "America/Montreal";
export const NOTIFICATION_KINDS = ["first", "escalation", "confirmation"] as const;

export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export type SubmissionMemberView = "validateurs" | "escalade" | "destinataires";

export type DeliveryState =
  | "pending"
  | "sending"
  | "accepted"
  | "delivered"
  | "failed"
  | "unknown"
  | "cancelled";

/** A Montréal calendar day, `yyyy-MM-dd`. */
export type LocalDay = string;

/** Historical JSONB retained for compatibility; never exposed or written by the module. */
export type SubmissionSignature =
  | { type: "drawn"; strokes: { points: [number, number, number][]; color?: string }[] }
  | { type: "typed"; name: string };

export function localDay(now: Date): LocalDay {
  return formatInTimeZone(now, SUBMISSION_TIMEZONE, "yyyy-MM-dd");
}

/** Noon UTC falls on the same calendar day in Montréal, whatever the offset. */
function noonUtc(day: LocalDay): Date {
  return new Date(day + "T12:00:00Z");
}

export function formatDay(day: LocalDay | null | undefined): string {
  return formatDate(day ? noonUtc(day) : null);
}

const LONG_DAY = new Intl.DateTimeFormat("fr-CA", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "UTC",
});

/** "1er novembre 2026": French writes the first of the month as an ordinal. */
function longDay(day: LocalDay) {
  return LONG_DAY.format(noonUtc(day)).replace(/^1 /, "1er ");
}

/** "du 15 au 21 octobre 2026", "du 22 octobre 2026 au 19 janvier 2027". */
export function formatPeriod(from: LocalDay, until: LocalDay): string {
  const end = longDay(until);
  const start = longDay(from);

  if (from.slice(0, 7) === until.slice(0, 7)) return `du ${start.split(" ")[0]} au ${end}`;

  return from.slice(0, 4) === until.slice(0, 4)
    ? `du ${start.replace(/ \d{4}$/, "")} au ${end}`
    : `du ${start} au ${end}`;
}

/** "10 h 00" in Montréal time. */
export function formatTime(at: Date | string): string {
  return formatInTimeZone(new Date(at), SUBMISSION_TIMEZONE, "H 'h' mm");
}

/** "2026-10-09 10:00:03 (Montréal)": exact instant for a trail entry's tooltip. */
export function formatInstant(at: Date | string): string {
  return formatInTimeZone(new Date(at), SUBMISSION_TIMEZONE, "yyyy-MM-dd HH:mm:ss '(Montréal)'");
}

export function weekday(day: LocalDay): boolean {
  const value = noonUtc(day).getUTCDay();

  return value !== 0 && value !== 6;
}

/** The calendar day `count` days after `day`, or before it when negative. */
export function addDays(day: LocalDay, count: number): LocalDay {
  const date = noonUtc(day);
  date.setUTCDate(date.getUTCDate() + count);

  return date.toISOString().slice(0, 10);
}

export function nextDay(day: LocalDay): LocalDay {
  return addDays(day, 1);
}

/** Montréal wall-clock deadline of each alert; the alert goes out at that minute. */
export const ALERT_DEADLINES = { first: "10:00", escalation: "12:00" } as const;

export type AlertKind = keyof typeof ALERT_DEADLINES;

export const ALERT_KINDS = ["first", "escalation"] as const satisfies readonly AlertKind[];

/** Alerts are queued this long before their deadline, so a late check visibly cancels them. */
export const ALERT_PREPARATION_MS = 15 * 60_000;

export function alertDeadline(day: LocalDay, kind: AlertKind): Date {
  return fromZonedTime(`${day}T${ALERT_DEADLINES[kind]}:00`, SUBMISSION_TIMEZONE);
}

/** Checks close at 17 h, Montréal time: neither the day nor a personal check is recorded later. */
export const CHECK_CLOSING = "17:00";

export function checkClosingTime(day: LocalDay): Date {
  return fromZonedTime(`${day}T${CHECK_CLOSING}:00`, SUBMISSION_TIMEZONE);
}

/** True once today's checks are closed. */
export function checksClosed(now: Date): boolean {
  return now >= checkClosingTime(localDay(now));
}

/** No check can be recorded for `date` any more: a past day, or today after 17 h. */
export function dayClosed(date: LocalDay, today: LocalDay, closed: boolean): boolean {
  return date < today || (date === today && closed);
}

/** Time left before an alert goes out, as `h:mm:ss`; never negative. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const pad = (n: number) => String(n).padStart(2, "0");

  return `${Math.floor(total / 3600)}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
}

/** Deadlines already passed today, without a check that would have met them. */
export function dueAlerts(now: Date): AlertKind[] {
  const day = localDay(now);

  if (!weekday(day)) return [];

  return ALERT_KINDS.filter((kind) => now >= alertDeadline(day, kind));
}

/** Alerts to queue now, each with the minute it must go out. */
export function preparedAlerts(now: Date): { kind: AlertKind; sendAt: Date }[] {
  const day = localDay(now);

  if (!weekday(day)) return [];

  return ALERT_KINDS.flatMap((kind) => {
    const sendAt = alertDeadline(day, kind);

    return now.getTime() >= sendAt.getTime() - ALERT_PREPARATION_MS ? [{ kind, sendAt }] : [];
  });
}

interface NotificationMember {
  validator?: boolean;
  first: boolean;
  escalation: boolean;
  confirmation: boolean;
  copyEscalation?: boolean;
  escalationConfirmation?: boolean;
  escalationReport?: boolean;
  copyReport?: boolean;
}

/** Action roles own their automatic emails; copy subscriptions add recipients. */
export function receivesNotification(member: NotificationMember, kind: NotificationKind): boolean {
  switch (kind) {
    case "first":
      return Boolean(member.validator || member.first);
    case "escalation":
      return Boolean(member.escalation || member.copyEscalation);
    case "confirmation":
      return Boolean(
        member.validator ||
        member.confirmation ||
        (member.escalation && member.escalationConfirmation),
      );
  }
}

/** The quarterly report: an escalation contact's choice, or a copy subscription. */
export function receivesReport(
  member: Pick<NotificationMember, "escalation" | "escalationReport" | "copyReport">,
): boolean {
  return Boolean((member.escalation && member.escalationReport) || member.copyReport);
}

/** At least one automatic or optional email. */
export function isRecipient(member: NotificationMember) {
  return (
    NOTIFICATION_KINDS.some((kind) => receivesNotification(member, kind)) || receivesReport(member)
  );
}

export function isCopyRecipient(member: NotificationMember) {
  return Boolean(member.first || member.copyEscalation || member.confirmation || member.copyReport);
}

export const DELIVERY_LABELS = {
  pending: "En attente",
  sending: "Envoi en cours",
  accepted: "Accepté",
  delivered: "Livré",
  failed: "Échec",
  unknown: "À vérifier",
  cancelled: "Annulé",
} satisfies Record<DeliveryState, string>;

export const SUBMISSION_EVENT_TYPES = [
  "prepared",
  "restored",
  "deadline_missed",
  "sent",
  "attempt_failed",
  "interrupted",
  "delivered",
  "bounced",
  "expired",
  "blocked",
  "cancelled",
  "validated",
  "checked_in",
  "admin_validated",
  "admin_skipped",
  "holiday_added",
  "holiday_removed",
  "module_enabled",
  "module_paused",
  "start_changed",
  "notifications_paused",
  "notifications_resumed",
] as const;

export type SubmissionEventType = (typeof SUBMISSION_EVENT_TYPES)[number];

/** Emails are suspended through `notificationsPausedUntil`, inclusive. */
export function notificationsPaused(
  config: { notificationsPausedUntil: LocalDay | null } | undefined,
  day: LocalDay,
): boolean {
  return Boolean(config?.notificationsPausedUntil && day <= config.notificationsPausedUntil);
}

export const KIND_LABELS = {
  first: "Alerte de 10 h",
  escalation: "Escalade de 12 h",
  confirmation: "Confirmation",
} satisfies Record<NotificationKind, string>;

export function dayStatus(
  day: {
    date: string;
    validatedAt: string | null;
    cancelled: boolean;
    holidayName?: string | null;
    skippedAt?: Date | string | null;
    validatedByAdmin?: boolean;
  },
  today: string,
  /** Today's checks are closed (after 17 h). */
  closed = false,
) {
  if (day.skippedAt) return "Ignoré par l’administration";

  if (day.validatedAt && day.validatedByAdmin) return "Validé par l’administration";

  if (day.validatedAt)
    return dueAlerts(new Date(day.validatedAt)).length ? "Validé en retard" : "Validé";

  if (day.holidayName) return "Congé";

  if (day.cancelled) return "Suspendu";

  return dayClosed(day.date, today, closed) ? "Non vérifié" : "À vérifier";
}
